import './PackageMessages.scss';

import { CommentOutlined, SendOutlined, SyncOutlined } from '@ant-design/icons';
import { useSelect } from '@refinedev/antd';
import { Badge, Button, Flex, Form, Mentions, Spin } from 'antd';
import { OptionProps } from 'antd/lib/mentions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AnchorableDrawer from '@/components/general/Drawers/AnchorableDrawer';
import { MESSAGE_TYPES, PackageMessage } from '@/model/Logistic/PackageMessage';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import PackageMessageRow from './PackageMessageRow';

interface PackageMessagesProps {
  packageId: number;
  type: 'shipment' | 'reception';
}

interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  username?: string;
}

interface MentionOption {
  label: string;
  value: string;
  key?: string;
  dataId: number;
}

const PackageMessages = (props: PackageMessagesProps) => {
  const { packageId, type } = props;
  const { shipmentRepository, receptionRepository } = useRepositoriesContext();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [anchoredDrawer, setAnchoredDrawer] = useState(false);
  const [mentionIds, setMentionIds] = useState<number[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesContainer = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<PackageMessage[]>([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const repository = useMemo(
    () => (type === 'shipment' ? shipmentRepository : receptionRepository),
    [type, shipmentRepository, receptionRepository],
  );
  const requestScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(true);
  }, []);
  /**
   * Busqueda y envío de mensajes
   */
  const fetchMessages = useCallback(async () => {
    setLoadingMessages(true);
    const fetchedMessages = await repository.getMessages(packageId);

    setMessages(fetchedMessages);
    setLoadingMessages(false);
  }, [packageId, repository]);
  const handleSubmit = useCallback(
    (values: { message_to_send: string }) => {
      setSendingMessage(true);
      void repository
        .postMessage(packageId, {
          package: packageId,
          text: values.message_to_send,
          destination_users_data: mentionIds ?? [],
          type_message: MESSAGE_TYPES.CHAT, // Ajusta el valor según corresponda
        })
        .then(() => {
          // Refrescar mensajes
          void fetchMessages().then(() => {
            form.resetFields();
            setMentionIds([]);
            setSendingMessage(false);
            requestScrollToBottom();
          });
        });
    },
    [packageId, repository, mentionIds, fetchMessages, form, requestScrollToBottom],
  );

  useEffect(() => {
    void fetchMessages().then(() => {
      requestScrollToBottom();
    });
  }, [fetchMessages, requestScrollToBottom]);

  /**
   * Manejo de menciones
   */

  const { selectProps: usersSelectProps } = useSelect<UserOption>({
    resource: 'users',
    optionValue: 'id',
    optionLabel: user => `${user.first_name} ${user.last_name}`,
    pagination: {
      mode: 'off',
      pageSize: 3000,
    },
  });
  const mentionOptions = useMemo<MentionOption[]>(
    () =>
      (usersSelectProps.options ?? [])
        .map(option => {
          let normalizedLabel = '';
          const optionLabel = (option as OptionProps).label;

          if (typeof optionLabel === 'string') {
            normalizedLabel = optionLabel;
          } else if (optionLabel && typeof optionLabel === 'object' && 'toString' in optionLabel) {
            normalizedLabel = optionLabel.toString();
          }

          const rawId = typeof option.value === 'number' ? option.value : Number(option.value);

          if (!normalizedLabel || Number.isNaN(rawId)) {
            return undefined;
          }

          return {
            label: normalizedLabel,
            value: normalizedLabel,
            key: option.value?.toString(),
            dataId: rawId,
          } as MentionOption;
        })
        .filter((option): option is MentionOption => !!option),
    [usersSelectProps.options],
  );
  const messageValue = Form.useWatch('message_to_send', form) ?? '';
  const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  useEffect(() => {
    if (!messageValue) {
      setMentionIds([]);

      return;
    }

    const ids = mentionOptions.reduce<Set<number>>((acc, option) => {
      const normalizedLabel = option.label.trim();

      if (!normalizedLabel) {
        return acc;
      }

      const pattern = new RegExp(`@${escapeRegExp(normalizedLabel)}(?=$|\\s|[.,!?;:])`);

      if (pattern.test(messageValue)) {
        acc.add(option.dataId);
      }

      return acc;
    }, new Set<number>());

    setMentionIds(Array.from(ids));
  }, [messageValue, mentionOptions]);

  // Ordenar mensajes por fecha
  const messageToShow = useMemo(() => {
    if (!messages) {
      return [];
    }

    const getTime = (date?: string) => {
      if (!date) {
        return Number.POSITIVE_INFINITY;
      }

      const parsedTime = new Date(date).getTime();

      return Number.isNaN(parsedTime) ? Number.POSITIVE_INFINITY : parsedTime;
    };

    return [...messages].sort((a, b) => getTime(a.date) - getTime(b.date));
  }, [messages]);

  useEffect(() => {
    if (!shouldScrollToBottom) {
      return;
    }

    if (!open || loadingMessages || !messagesContainer.current || messages.length === 0) {
      return;
    }

    messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight;
    setShouldScrollToBottom(false);
  }, [shouldScrollToBottom, open, loadingMessages, messages]);

  return (
    <div>
      <Badge count={messageToShow.length} size="small">
        <Button
          icon={<CommentOutlined />}
          type={open ? 'primary' : 'default'}
          onClick={() => setOpen(!open)}
        />
      </Badge>
      <AnchorableDrawer
        classNames={{ body: 'PackageMessagesDrawerBody' }}
        className="PackageMessagesDrawer"
        title={
          <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            {'Mensajes del paquete'} {packageId ? `#${packageId}` : ''}{' '}
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => void fetchMessages().then(() => requestScrollToBottom())}
            />
          </Flex>
        }
        width={500}
        open={open}
        setOpen={setOpen}
        anchorableDrawer={anchoredDrawer}
        setAnchorableDrawer={setAnchoredDrawer}>
        <div className="PackageMessagesDrawerMessages" ref={messagesContainer}>
          {loadingMessages ? (
            <Spin />
          ) : (
            messageToShow.map((message, index) => (
              <PackageMessageRow key={index} message={message} />
            ))
          )}
        </div>
        <Form form={form} className="PackageMessagesDrawerInputContainer" onFinish={handleSubmit}>
          <Form.Item name={'message_to_send'} className="PackageMessagesDrawerInput">
            <Mentions
              disabled={sendingMessage}
              onPressEnter={e => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  form.submit();
                }
              }}
              autoSize={{ minRows: 2 }}
              placeholder="Escribe tu mensaje aquí..."
              options={mentionOptions}
              loading={usersSelectProps.loading}
              filterOption={(search, option) => {
                const normalizedSearch = search.toLowerCase();

                if (!normalizedSearch) {
                  return true;
                }

                const normalizedLabel = (option as OptionProps)?.label
                  ? (option as OptionProps).label.toString().toLowerCase()
                  : '';

                return normalizedLabel.includes(normalizedSearch);
              }}
            />
          </Form.Item>
          <Button
            className="PackageMessagesDrawerSendButton"
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={sendingMessage}
            disabled={sendingMessage}
          />
        </Form>
      </AnchorableDrawer>
    </div>
  );
};

export default PackageMessages;
